import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { cn } from "@/utils/cn";

export interface DonutSlice {
  label: string;
  value: number;
  color: string;
}

export function DonutChart({
  data,
  valueFormatter = (v) => String(v),
  height = 220,
  className,
}: {
  data: DonutSlice[];
  valueFormatter?: (value: number) => string;
  height?: number;
  className?: string;
}) {
  const total = data.reduce((acc, curr) => acc + curr.value, 0);

  return (
    <div
      style={{ height }}
      className={cn("flex flex-col items-center justify-between sm:flex-row gap-4", className)}
    >
      {/* Donut graphic */}
      <div className="relative h-full w-full sm:w-1/2 flex items-center justify-center">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip
              formatter={(val: number, name: string) => {
                const pct = total > 0 ? ((val / total) * 100).toFixed(1) : "0";
                return [`${valueFormatter(val)} (${pct}%)`, name];
              }}
              contentStyle={{
                background: "#FCFBF7",
                border: "1px solid #E3DDCB",
                borderRadius: 8,
                fontSize: 12,
              }}
            />
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={75}
              paddingAngle={3}
              stroke="#FCFBF7"
              strokeWidth={2}
            >
              {data.map((entry, idx) => (
                <Cell key={`slice-${idx}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Small Legend */}
      <div className="flex w-full sm:w-1/2 flex-col justify-center gap-2.5 sm:pr-4">
        {data.map((slice) => {
          const pct = total > 0 ? ((slice.value / total) * 100).toFixed(0) : "0";
          return (
            <div
              key={slice.label}
              className="flex items-center justify-between gap-2 text-xs"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: slice.color }}
                />
                <span className="truncate font-medium text-ink-muted">
                  {slice.label}
                </span>
              </div>
              <div className="flex items-center gap-2 font-mono">
                <span className="font-semibold text-ink">
                  {valueFormatter(slice.value)}
                </span>
                <span className="rounded bg-cream-200 px-1.5 py-0.5 text-[10px] text-ink-faint">
                  {pct}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
